import React, { useEffect, useRef } from 'react'
import { FormikProps, getIn } from 'formik'
import { ErrorLabel, FileInput, Label } from '..'
import { FontSize } from '../../../_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'

interface FormFileInputProps {
  label?: string
  name: string
  formik: FormikProps<any>
  /**
   * Comma-separated list of accepted file types (e.g. `image/*`, `video/*`).
   */
  accept?: string
  /**
   * 'user' = front camera, 'environment' = back camera
   */
  capture?: 'user' | 'environment'
  isRequired?: boolean
  isDisabled?: boolean
  className?: string
  style?: React.CSSProperties
  labelSize?: FontSize
  infoText?: string
  multiple?: boolean
  highlight?: boolean
  entity?: string
}

export const FormFileInput: React.FC<FormFileInputProps> = ({
  label,
  name,
  formik,
  accept = '*/*',
  capture,
  isRequired = false,
  isDisabled = false,
  multiple = false,
  className = '',
  style = {},
  labelSize,
  infoText,
  highlight,
  entity,
}) => {
  const { t } = useT()
  const inputRef = useRef<HTMLInputElement | null>(null)

  const errorMessage = getIn(formik.errors, name)
  const touched = getIn(formik.touched, name)
  const value = getIn(formik.values, name)
  const resolvedLabel = label ?? (entity ? t(`${entity}.label`) : undefined)

  useEffect(() => {
    if ((!multiple && value === null) || (multiple && Array.isArray(value) && value.length === 0)) {
      if (inputRef.current) inputRef.current.value = ''
    }
  }, [value, multiple])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files
    if (!files) {
      formik.setFieldValue(name, multiple ? [] : null)
      return
    }

    if (multiple) {
      formik.setFieldValue(name, Array.from(files))
    } else {
      formik.setFieldValue(name, files[0])
    }
  }

  return (
    <div className={`${className}`}>
      {resolvedLabel && (
        <Label
          className='mb-2'
          isRequired={isRequired}
          size={labelSize}
          infoText={infoText}
          highlight={highlight}
        >
          {resolvedLabel}
        </Label>
      )}
      <FileInput
        onChange={handleChange}
        accept={accept}
        capture={capture}
        className={`${touched && errorMessage ? 'is-invalid' : touched ? 'is-valid' : ''}`}
        style={style}
        isDisabled={isDisabled}
        multiple={multiple}
        ref={inputRef}
      />
      {touched && errorMessage && <ErrorLabel>{errorMessage}</ErrorLabel>}
    </div>
  )
}
